import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { earnedWeightsSchema, type EarnedWeights } from '@forecastify/shared';
import { useT } from '../../i18n';
import { useLiveInvalidation } from '../../live/use-live';
import { useSession } from '../../session';
import { SettingsPage } from './settings-page';
import { SettingsTable, type SettingsRow } from './settings-table';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 30 Oct 2026 · EV-2 · Tasks → Weights: how much of an estimate each status  │
 * │ category has earned.                                                       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE SAME TABLE AS ITS TWO NEIGHBOURS — the product owner.** The first version was a form
 * card under the tabs and read as a different screen. It is now a `SettingsTable` like Statuses and
 * Priorities: one row per category, the weight typed into the row (borderless at rest, saved on
 * leaving the field, as the FX and leave grids are), open and closed marked *fixed*.
 *
 * ⚠️ Shown as percentages, stored as fractions (`0248`).
 */
export function WeightsPanel({
  tabs,
  canManage,
}: {
  readonly tabs: ReactNode;
  readonly canManage: boolean;
}) {
  const t = useT();
  const session = useSession();
  const [weights, setWeights] = useState<EarnedWeights | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    const response = await session.request('/task-statuses/weights').catch(() => null);
    if (response?.ok !== true) {
      setError(t('error.unavailable'));
      return;
    }
    const parsed = earnedWeightsSchema.safeParse(await response.json());
    if (parsed.success) setWeights(parsed.data);
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug); depending on it refetches for ever. */
  }, [session.workspaceId]);

  useEffect(() => {
    void reload();
  }, [reload]);
  useLiveInvalidation(['task_status'], () => {
    void reload();
  });

  const save = async (patch: Partial<EarnedWeights>): Promise<void> => {
    if (weights === null) return;
    setError(null);
    const response = await session
      .request('/task-statuses/weights', {
        method: 'PUT',
        body: JSON.stringify({ ...weights, ...patch }),
      })
      .catch(() => null);
    if (response?.ok !== true) {
      setError(t('error.unavailable'));
      return;
    }
    const parsed = earnedWeightsSchema.safeParse(await response.json());
    if (parsed.success) setWeights(parsed.data);
  };

  /* Percent on the screen, a fraction on the wire — one place converts. A value that is not a
     number between 0 and 100 is not sent; the cell goes back to what was saved. */
  const percent = (value: number): string => String(Math.round(value * 100));
  const commit = (key: keyof EarnedWeights, text: string): void => {
    const number = Number(text.replace(',', '.').replace('%', '').trim());
    if (!Number.isFinite(number) || number < 0 || number > 100) {
      setWeights((current) => (current === null ? null : { ...current }));
      return;
    }
    void save({ [key]: Math.round(number) / 100 });
  };

  /* One row shape for all four, so the figures line up: a fixed one is the same cell, disabled,
     with *fixed* under its name. */
  const row = (
    id: string,
    label: string,
    value: string,
    onCommit?: (next: string) => void,
  ): SettingsRow => ({
    id,
    cells: [
      {
        kind: 'name',
        text: label,
        ...(onCommit === undefined ? { sub: t('settings.weights.fixed') } : {}),
      },
      {
        kind: 'input',
        value,
        ariaLabel: t('settings.weights.ariaLabel', { category: label }),
        disabled: onCommit === undefined || !canManage,
        onCommit: onCommit ?? (() => undefined),
      },
    ],
  });
  const editableRow = (id: keyof EarnedWeights, label: string): SettingsRow =>
    row(id, label, weights === null ? '' : percent(weights[id]), (next) => {
      commit(id, next);
    });

  const rows: SettingsRow[] =
    weights === null
      ? []
      : [
          row('open', t('status.category.open'), '0'),
          editableRow('inProgress', t('status.category.inProgress')),
          editableRow('inReview', t('status.category.inReview')),
          row('closed', t('status.category.closed'), '100'),
        ];

  return (
    <SettingsPage title={t('settings.nav.tasks')} tabs={tabs} error={error}>
      <SettingsTable
        grid="minmax(0,1fr) 120px"
        minWidth={360}
        columns={[
          { label: t('settings.statuses.column.category') },
          { label: t('settings.weights.column'), align: 'right' },
        ]}
        groups={[{ rows }]}
        loading={weights === null && error === null}
        empty={{
          icon: 'sliders-horizontal',
          title: t('settings.weights.title'),
          body: t('settings.weights.lede'),
        }}
      />
    </SettingsPage>
  );
}
